/**
 * Error Logging Service
 *
 * Centralized error logging for the frontend application.
 * Sends errors to the backend API for persistence and monitoring.
 *
 * Features:
 * - Browser error tracking (JS errors, unhandled rejections)
 * - Network error tracking (API failures)
 * - User context capture (_user ID, session)
 * - User action tracking (clicks, submits, etc.)
 * - Navigation history tracking
 * - Component context tracking
 * - Error deduplication
 * - Batched sending for performance
 *
 * @version 2.0
 * @created 2025-11-19
 * @updated 2025-11-20 - Added _user action and navigation tracking
 */

class ErrorLoggingService {
  constructor() {
    // Use relative URL to leverage Vite proxy
    this.endpoint = '/api/v1/errors/log';
    this.errorQueue = [];
    this.maxQueueSize = 10;
    this.flushInterval = 5000; // 5 seconds
    this.isInitialized = false;
    this.recentErrors = new Set(); // For deduplication
    this.dedupeWindow = 60000; // 1 minute

    // User action tracking
    this.lastAction = null;
    this.actionTimeout = null;
    this.actionWindow = 5000; // 5 seconds - consider action relevant if error within this time

    // Navigation history tracking
    this.navigationHistory = [];
    this.maxNavigationHistory = 10;
    this.currentRouteStartTime = Date.now();

    // Component context tracking
    this.currentComponent = null;
  }

  /**
   * Initialize error logging service
   * Sets up global error handlers and _user interaction tracking
   */
  initialize() {
    if (this.isInitialized) {
      return;
    }

    // Handle uncaught JavaScript errors
    window.addEventListener('error', (event) => {
      this.logError({
        type: 'uncaught_error',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });
    });

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        type: 'unhandled_rejection',
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack,
      });
    });

    // Track _user clicks
    document.addEventListener('click', (event) => {
      this.trackAction('click', event.target, event);
    }, true); // Use capture phase to catch all clicks

    // Track form submissions
    document.addEventListener('submit', (event) => {
      this.trackAction('submit', event.target, event);
    }, true);

    // Flush queue periodically
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.flushInterval);

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flush(true); // Synchronous flush
    });

    this.isInitialized = true;
  }

  /**
   * Track _user action (click, submit, etc.)
   *
   * @param {string} actionType - Type of action (click, submit, scroll, etc.)
   * @param {HTMLElement} target - Target element
   * @param {Event} event - Event object
   */
  trackAction(actionType, target, event = null) {
    try {
      // Clear previous action timeout
      if (this.actionTimeout) {
        clearTimeout(this.actionTimeout);
      }

      // Get element identifier
      const elementId = target.id || target.getAttribute('data-testid') || target.className;
      const elementTag = target.tagName?.toLowerCase();

      // Store action details
      this.lastAction = {
        type: actionType,
        target: elementId || elementTag,
        element: elementTag,
        timestamp: new Date().toISOString(),
        coordinates: event ? { x: event.clientX, y: event.clientY } : null,
      };

      // Clear action after window expires
      this.actionTimeout = setTimeout(() => {
        this.lastAction = null;
      }, this.actionWindow);
    } catch (err) {
      // Don't let action tracking break the app
      console.warn('[ErrorLogging] Failed to track action:', err);
    }
  }

  /**
   * Track navigation to a new route
   *
   * @param {string} route - Route path
   * @param {Object} metadata - Additional metadata
   */
  trackNavigation(route, metadata = {}) {
    try {
      const now = Date.now();
      const duration = now - this.currentRouteStartTime;

      // Add current route to history before navigating
      if (this.navigationHistory.length > 0 || route !== window.location.pathname) {
        const currentRoute = this.navigationHistory[this.navigationHistory.length - 1];
        if (currentRoute) {
          currentRoute.duration = duration;
        }
      }

      // Add new route to history
      this.navigationHistory.push({
        route: this.sanitizeUrl(route),
        timestamp: new Date().toISOString(),
        referrer: document.referrer ? this.sanitizeUrl(document.referrer) : null,
        ...metadata,
      });

      // Limit history size
      if (this.navigationHistory.length > this.maxNavigationHistory) {
        this.navigationHistory.shift();
      }

      // Reset timer for new route
      this.currentRouteStartTime = now;
    } catch (err) {
      console.warn('[ErrorLogging] Failed to track navigation:', err);
    }
  }

  /**
   * Set current component context
   *
   * @param {string} componentName - Component name
   * @param {Object} metadata - Component metadata (props, state, etc.)
   */
  setComponentContext(componentName, metadata = {}) {
    try {
      this.currentComponent = {
        name: componentName,
        route: window.location.pathname,
        timestamp: new Date().toISOString(),
        ...metadata,
      };
    } catch (err) {
      console.warn('[ErrorLogging] Failed to set component context:', err);
    }
  }

  /**
   * Clear component context
   */
  clearComponentContext() {
    this.currentComponent = null;
  }

  /**
   * Sanitize URL (remove sensitive query parameters)
   *
   * @param {string} url - URL to sanitize
   * @returns {string} Sanitized URL
   */
  sanitizeUrl(url) {
    try {
      const urlObj = new URL(url, window.location.origin);
      const sensitiveParams = ['token', 'access_token', 'refresh_token', 'api_key', 'password', 'email', 'code'];

      sensitiveParams.forEach(param => {
        if (urlObj.searchParams.has(param)) {
          urlObj.searchParams.set(param, '[REDACTED]');
        }
      });

      return urlObj.toString().replace(window.location.origin, '');
    } catch (err) {
      // If URL parsing fails, return the original
      return url;
    }
  }

  /**
   * Log an error
   *
   * @param {Object} error - Error object or error details
   * @param {Object} context - Additional context (_user, route, etc.)
   */
  logError(error, context = {}) {
    try {
      const errorDetails = this.formatError(error, context);

      // Deduplicate errors
      const errorKey = this.getErrorKey(errorDetails);
      if (this.recentErrors.has(errorKey)) {
        console.log('[ErrorLogging] Duplicate error suppressed:', errorKey);
        return;
      }

      // Add to recent errors for deduplication
      this.recentErrors.add(errorKey);
      setTimeout(() => {
        this.recentErrors.delete(errorKey);
      }, this.dedupeWindow);

      // Add to queue
      this.errorQueue.push(errorDetails);

      // Flush if queue is full
      if (this.errorQueue.length >= this.maxQueueSize) {
        this.flush();
      }
    } catch (err) {
      // Don't let error logging break the app
      // Silently fail to avoid infinite error loops
    }
  }

  /**
   * Log a route error (React Router errors)
   *
   * @param {Error} error - Route error
   * @param {Object} location - Route location
   */
  logRouteError(error, location = {}) {
    this.logError(error, {
      type: 'route_error',
      route: location.pathname,
      search: location.search,
      hash: location.hash,
      state: location.state,
    });
  }

  /**
   * Log an API error (network failures)
   *
   * @param {Object} error - API error details
   * @param {Object} request - Request details (URL, method, etc.)
   */
  logAPIError(error, request = {}) {
    this.logError(error, {
      type: 'api_error',
      url: request.url,
      method: request.method,
      status: error.status,
      statusText: error.statusText,
    });
  }

  /**
   * Format error for logging
   *
   * @param {Error|Object} error - Error to format
   * @param {Object} context - Additional context
   * @returns {Object} Formatted error
   */
  formatError(error, context = {}) {
    const formatted = {
      // Error details
      message: error.message || error.reason || String(error),
      stack: error.stack || null,
      type: error.name || error.type || context.type || 'error',

      // Browser context
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),

      // Application context
      environment: import.meta.env.VITE_ENV || 'production',
      version: import.meta.env.VITE_APP_VERSION || 'unknown',

      // Component context (structured columns)
      componentName: this.currentComponent?.name || context.componentName || null,
      componentPath: this.currentComponent?.path || context.componentPath || null,
      route: context.route || window.location.pathname,

      // User action (structured columns)
      userAction: this.lastAction?.type || context.userAction || null,
      userActionTarget: this.lastAction?.target || context.userActionTarget || null,

      // Additional context metadata (JSONB column)
      contextMetadata: {
        // Navigation history
        navigationHistory: this.navigationHistory.length > 0 ? this.navigationHistory : null,

        // User action details
        userActionDetails: this.lastAction ? {
          element: this.lastAction.element,
          coordinates: this.lastAction.coordinates,
          timestamp: this.lastAction.timestamp,
        } : null,

        // Component details
        componentProps: this.currentComponent?.props || context.componentProps || null,

        // Time on current page
        timeOnPage: Date.now() - this.currentRouteStartTime,

        // Custom context from caller
        ...context.metadata,
      },

      // Additional context (backwards compatibility)
      ...context,
    };

    // Add _user context if available
    try {
      const userStr = localStorage.getItem('_user');
      if (userStr) {
        const _user = JSON.parse(userStr);
        formatted.userId = _user.id;
        formatted.userEmail = _user.email;
        formatted.userType = _user.userType;
      }
    } catch (err) {
      // Ignore localStorage errors
    }

    // Sanitize sensitive data
    formatted.message = this.sanitizeMessage(formatted.message);
    if (formatted.stack) {
      formatted.stack = this.sanitizeStack(formatted.stack);
    }

    // Clean up null/empty contextMetadata
    if (formatted.contextMetadata) {
      Object.keys(formatted.contextMetadata).forEach(key => {
        // eslint-disable-next-line security/detect-object-injection -- safe: key is from Object.keys() iteration
        if (formatted.contextMetadata[key] === null || formatted.contextMetadata[key] === undefined) {
          // eslint-disable-next-line security/detect-object-injection -- safe: key is from Object.keys() iteration
          delete formatted.contextMetadata[key];
        }
      });

      if (Object.keys(formatted.contextMetadata).length === 0) {
        formatted.contextMetadata = null;
      }
    }

    return formatted;
  }

  /**
   * Generate a unique key for error deduplication
   *
   * @param {Object} error - Error details
   * @returns {string} Unique key
   */
  getErrorKey(error) {
    return `${error.type}:${error.message}:${error.url}`;
  }

  /**
   * Sanitize error message (remove sensitive data)
   *
   * @param {string} message - Error message
   * @returns {string} Sanitized message
   */
  sanitizeMessage(message) {
    if (!message) {return '';}

    // Remove email addresses
    message = message.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL]');

    // Remove JWT tokens
    message = message.replace(/eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*/g, '[JWT_TOKEN]');

    // Remove API keys
    message = message.replace(/[a-zA-Z0-9_-]{32,}/g, '[API_KEY]');

    return message;
  }

  /**
   * Sanitize stack trace (remove sensitive data)
   *
   * @param {string} stack - Stack trace
   * @returns {string} Sanitized stack trace
   */
  sanitizeStack(stack) {
    if (!stack) {return '';}

    // Remove full file paths (keep only filenames)
    stack = stack.replace(/(\w+:\/\/[^:\s]+\/)/g, '');

    return stack;
  }

  /**
   * Flush error queue to server
   *
   * @param {boolean} sync - Use synchronous request (for page unload)
   */
  flush(sync = false) {
    if (this.errorQueue.length === 0) {
      return;
    }

    const errors = [...this.errorQueue];
    this.errorQueue = [];

    if (sync) {
      // Synchronous request for page unload
      const blob = new Blob([JSON.stringify({ errors })], {
        type: 'application/json',
      });
      navigator.sendBeacon(this.endpoint, blob);
    } else {
      // Asynchronous request
      fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ errors }),
      }).catch((err) => {
        console.error('[ErrorLogging] Failed to send errors:', err);
        // Put errors back in queue for retry
        this.errorQueue.unshift(...errors);
      });
    }
  }

  /**
   * Clean up service
   */
  cleanup() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush(true);
  }
}

// Create singleton instance
const errorLoggingService = new ErrorLoggingService();

// Export singleton
export default errorLoggingService;

/**
 * Convenience function to log errors
 *
 * @param {Error|Object} error - Error to log
 * @param {Object} context - Additional context
 */
export function logError(error, context = {}) {
  errorLoggingService.logError(error, context);
}

/**
 * Convenience function to log route errors
 *
 * @param {Error} error - Route error
 * @param {Object} location - Route location
 */
export function logRouteError(error, location = {}) {
  errorLoggingService.logRouteError(error, location);
}

/**
 * Convenience function to log API errors
 *
 * @param {Object} error - API error
 * @param {Object} request - Request details
 */
export function logAPIError(error, request = {}) {
  errorLoggingService.logAPIError(error, request);
}

/**
 * Convenience function to track navigation
 *
 * @param {string} route - Route path
 * @param {Object} metadata - Additional metadata
 */
export function trackNavigation(route, metadata = {}) {
  errorLoggingService.trackNavigation(route, metadata);
}

/**
 * Convenience function to set component context
 *
 * @param {string} componentName - Component name
 * @param {Object} metadata - Component metadata
 */
export function setComponentContext(componentName, metadata = {}) {
  errorLoggingService.setComponentContext(componentName, metadata);
}

/**
 * Convenience function to clear component context
 */
export function clearComponentContext() {
  errorLoggingService.clearComponentContext();
}
